const React = require('react');
const { default: Styled, useTheme } = require('styled-components');
const UniqueId = require('lodash/uniqueId');
const PropTypes = require('prop-types');
const { Controller } = require('react-hook-form');
const { matchSorter } = require('match-sorter');
const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Button } = require('@mui/material/Button');
const { default: Autocomplete } = require('@mui/material/Autocomplete');
const { NavLink } = require('react-router-dom');
const { default: clsx } = require('clsx');
const { default: Classes } = require('./styles.scss');
const Fonts = require('styles/fonts.json');
const useForm = require('hooks/useForm');
const SignupFormConfig = require('./formConfig');
const IsEmail = require('utils/is-email');
const { analyticsTemplates } = require('utils/analytics');
const Logomark = require('static/nearpeer_n_white.svg');
const NearpeerMobile = require('static/nearpeer-mobile.png');
const NearpeerMobileBusiness = require('static/nearpeer-mobile-business-no-bg.png');
// iOS-only image is for app store compliance
const NearpeerMobileiOS = require('static/nearpeer-mobile-ios.png');
const NearpeerMobileBusinessiOS = require('static/nearpeer-mobile-business-ios-new.png');
const PlayStore = require('static/google_play.svg');
const AppStore = require('static/app_store.svg');
const Snackbar = require('containers/Snackbar');
const ScrollContainerLayout = require('layouts/ScrollContainerLayout');
const Footer = require('components/Footer');
const FocusIndicator = require('components/FocusIndicator');
const { getDevice, isIOS, isUnknown } = require('utils/device');
const LoginTextField = require('components/LoginTextField');
const {
    shouldResetRememberedEmailContext,
    buildRememberedEmailUpdate,
    buildLoginContextReset,
    buildSignupContextReset
} = require('services/auth-management/signup-state');

const internals = {};

const Signup = module.exports = function Signup(props) {

    const {
        useCallback,
        useEffect,
        useMemo,
        useRef,
        useState
    } = React;

    const {
        onSubmit,
        onInvalidSubmit,
        signupDetails,
        showSchoolList,
        text,
        secondaryText,
        tertiaryText,
        useWhiteLanding,
        useWideLogo,
        useExtraWideLogo,
        logoSidePadding,
        footer,
        logo,
        match: { params },
        customGraphic,
        customError,
        schools,
        isParentView = false,
        isCompany,
        isCommunity,
        updateLoginContext,
        updateSignupContext,
        rememberedEmail = ''
    } = props;

    const theme = useTheme();
    const isTeamsApp = process.env.APP_FLAVOUR === 'teams';
    const device = getDevice();
    const id = useRef(UniqueId('signup-'));
    const hasResetRememberedContext = useRef(false);
    const initialContextEmail = useRef(rememberedEmail || '');
    const {
        ParentTertiaryText,
        SchoolTip,
        SecondaryText,
        StyledButton,
        StyledForm,
        StyledPageBox,
        StyledLink,
        WideLogoContainer,
        Wrapper
    } = internals;

    const [emailErrorShow, setEmailErrorShow] = useState(false);
    const [schoolIdErrorShow, setSchoolIdErrorShow] = useState(false);
    const [schoolSearchText, setSchoolSearchText] = useState(() => (
        internals.getSchoolSearchText({
            schools,
            schoolId: signupDetails.schoolId
        })
    ));

    const defaults = useMemo(() => ({
        email: signupDetails.email || '',
        schoolId: signupDetails.schoolId || null
    }), [signupDetails.email, signupDetails.schoolId]);

    const formConfig = useMemo(() => SignupFormConfig.buildFormConfig({
        onSubmit: internals.noop,
        defaults
    }), [defaults]);

    const { methods } = useForm(formConfig);

    const {
        control,
        getValues,
        setValue,
        watch
    } = methods;

    const email = watch('email') || '';
    const schoolId = watch('schoolId');

    const filteredSchools = useMemo(() => {

        if (!schools) {
            return [];
        }

        return schools
        .filter((school) => (
            isTeamsApp ? school.isCompany : !school.isCompany
        ))
        .sort((firstSchool, secondSchool) => (
            String(firstSchool.name || '').localeCompare(String(secondSchool.name || ''))
        ));
    }, [isTeamsApp, schools]);

    const slug = params.slug || '';
    const isAsga = slug === 'asga';
    const isUmaine = slug === 'umaine';
    const isForbiddenEmail = email.includes('@txstate') ||
        email.includes('@umsystem') ||
        email.includes('@fresnostate');
    const showLoyolaHintText = filteredSchools &&
        schoolSearchText &&
        schoolSearchText.toLowerCase().includes('loyola');

    useEffect(() => {

        document.body.style.backgroundColor = theme.palette.primary.main;

        return () => {

            document.body.style.backgroundColor = 'unset';
        };
    }, [theme.palette.primary.main]);

    useEffect(() => {

        if (signupDetails && signupDetails.schoolId) {
            setValue('schoolId', signupDetails.schoolId);
            setSchoolSearchText(internals.getSchoolSearchText({
                schools,
                schoolId: signupDetails.schoolId
            }));
        }
    }, [schools, setValue, signupDetails]);

    const getFormValues = useCallback((nextValues = {}) => {

        const values = {
            email,
            schoolId,
            ...nextValues
        };

        return internals.buildFormState({
            values,
            emailErrorShow: typeof nextValues.emailErrorShow === 'boolean'
                ? nextValues.emailErrorShow
                : emailErrorShow,
            schoolIdErrorShow: typeof nextValues.schoolIdErrorShow === 'boolean'
                ? nextValues.schoolIdErrorShow
                : schoolIdErrorShow,
            schoolSearchText
        });
    }, [email, emailErrorShow, schoolId, schoolIdErrorShow, schoolSearchText]);

    const validateField = useCallback((field, nextValues = {}) => {

        const values = {
            email,
            schoolId,
            ...nextValues
        };

        return internals.validateField({
            field,
            values,
            showSchoolList,
            schoolSearchText
        });
    }, [email, schoolId, schoolSearchText, showSchoolList]);

    const showError = useCallback((field, show = true) => () => {

        if (field === 'email') {
            setEmailErrorShow(show);
            return;
        }

        if (field === 'schoolId') {
            setSchoolIdErrorShow(show);
        }
    }, []);

    const error = useCallback((field) => {

        const shouldShow = field === 'email' ? emailErrorShow : schoolIdErrorShow;

        if (!shouldShow) {
            return null;
        }

        const fieldError = validateField(field);

        if (fieldError !== null) {
            return (
                <div
                    id={`${id.current}-${field}-error-message`}
                    role='alert'
                    aria-live='assertive'
                >
                    {fieldError}
                </div>
            );
        }

        return null;
    }, [emailErrorShow, schoolIdErrorShow, validateField]);

    const hasError = useCallback((field) => (
        validateField(field) !== null
    ), [validateField]);

    const getDescribedById = useCallback((field) => {

        if (hasError(field)) {
            return `${id.current}-${field}-error-message`;
        }

        return null;
    }, [hasError]);

    const onEmailChange = useCallback((evt) => {

        const nextEmail = evt.target.value;
        const currentSchoolId = getValues('schoolId');

        if (!hasResetRememberedContext.current && shouldResetRememberedEmailContext({
            initialEmail: initialContextEmail.current,
            nextEmail
        })) {
            hasResetRememberedContext.current = true;

            updateSignupContext(buildSignupContextReset({
                ...(signupDetails.redirect ? {
                    redirect: signupDetails.redirect
                } : {}),
                ...(signupDetails.redirectTarget ? {
                    redirectTarget: signupDetails.redirectTarget
                } : {}),
                email: nextEmail,
                schoolId: currentSchoolId
            }));
            updateLoginContext(buildLoginContextReset({
                ...(signupDetails.redirect ? {
                    redirect: signupDetails.redirect
                } : {}),
                ...(signupDetails.redirectTarget ? {
                    redirectTarget: signupDetails.redirectTarget
                } : {}),
                email: nextEmail
            }));
        }
        else {
            const emailUpdate = hasResetRememberedContext.current
                ? buildRememberedEmailUpdate({ email: nextEmail })
                : { email: nextEmail };

            updateSignupContext(emailUpdate);

            if (hasResetRememberedContext.current) {
                updateLoginContext(emailUpdate);
            }
        }

        setValue('email', nextEmail, {
            shouldDirty: true,
            shouldTouch: true
        });
    }, [getValues, setValue, updateLoginContext, updateSignupContext]);

    const onSchoolSearchTextChange = useCallback((evt, nextValue) => {

        setSchoolSearchText(typeof nextValue === 'string' ? nextValue : evt?.target?.value ?? '');
    }, []);

    const onSchoolChange = useCallback((event, school) => {

        const nextSchool = school || { id: null, name: null };
        const { id: nextSchoolId, name } = nextSchool;

        setValue('schoolId', nextSchoolId, {
            shouldDirty: true,
            shouldTouch: true
        });
        setSchoolSearchText(name || '');
        setSchoolIdErrorShow(false);
    }, [setValue]);

    const onValidSubmit = useCallback(async () => {

        analyticsTemplates.buttons(
            'create account',
            slug
                ? `landing page ${slug}: create account`
                : 'landing page: create account'
        );

        try {
            const res = await onSubmit(getFormValues());

            if (!res) {
                setEmailErrorShow(true);
            }
        }
        catch (err) {

            console.log('Signup error:', err);

            setEmailErrorShow(true);
        }
    }, [getFormValues, onSubmit, slug]);

    const submitOrError = useCallback(async () => {

        const nextEmailErrorShow = Boolean(validateField('email'));
        const nextSchoolIdErrorShow = Boolean(validateField('schoolId'));

        setEmailErrorShow(nextEmailErrorShow);
        setSchoolIdErrorShow(nextSchoolIdErrorShow);

        if (nextEmailErrorShow || nextSchoolIdErrorShow) {
            FocusIndicator.onBlurHandler();

            return onInvalidSubmit(getFormValues({
                emailErrorShow: nextEmailErrorShow,
                schoolIdErrorShow: nextSchoolIdErrorShow
            }));
        }

        return onValidSubmit();
    }, [getFormValues, onInvalidSubmit, onValidSubmit, validateField]);

    if (isUmaine) {
        require('../../../../scripts/umaineTracker');
    }

    return (
        <Wrapper>
            <ScrollContainerLayout>
                <StyledPageBox
                    component='div'
                    className={`${Classes.bodyContainer}`}
                    $useWhiteLanding={useWhiteLanding}
                >
                    <div className={Classes.homeMain} role='main'>
                        {!logo && (
                            <div className={Classes.logoContainer}>
                                <div className={Classes.logomarkContainer}>
                                    <img src={Logomark} alt='' className={Classes.logomark} />
                                    <div className={`${Classes.tm} ${useWhiteLanding ? Classes.csun : ''}`}>™</div>
                                </div>
                                <h1
                                    id='main-page-heading'
                                    tabIndex={0}
                                    className={`${Classes.header}
                                    ${useWhiteLanding ? Classes.csun : ''}`}
                                >
                                    Nearpeer
                                </h1>
                            </div>
                        )}

                        {logo && !useWideLogo && (
                            <div className={`${Classes.logosWrapper} ${useWhiteLanding ? Classes.csun : ''}`}>
                                <div className={Classes.nearpeerLogo}>
                                    <img className={Classes.logo} alt={slug} src={internals.nearpeerLogo} />
                                </div>
                                <h1 className={Classes.schoolLogo} tabIndex={0} data-focus-outline='radius:5,padding:2'>
                                    <img className={Classes.logo} alt={slug} src={logo} />
                                </h1>
                            </div>
                        )}

                        {logo && useWideLogo && (
                            <WideLogoContainer
                                className={`${Classes.logosWrapper} ${useWideLogo ? (useExtraWideLogo ? Classes.useExtraWideLogo : Classes.wideLogo) : ''}`}
                                $logoSidePadding={logoSidePadding}
                            >
                                <h1 className={`${Classes.schoolLogo} ${Classes.wideLogo}`} tabIndex={0} data-focus-outline='radius:5,padding:2'>
                                    <img className={Classes.logo} alt={slug} src={logo} />
                                </h1>
                            </WideLogoContainer>
                        )}

                        {text && <div className={`${Classes.subtitle} ${useWhiteLanding ? Classes.csun : ''} ${isAsga ? Classes.asga : ''}`}>{text}</div>}

                        <div className={clsx(Classes.homeContent, (isCompany || isCommunity) && Classes.alignMiddle)}>
                            <div className={Classes.mobileScreensContainer}>
                                    <img
                                        className={Classes.mobileScreens}
                                        src={(isCompany || isCommunity || isTeamsApp)
                                            ? (isIOS(device) ? NearpeerMobileBusinessiOS : (customGraphic || NearpeerMobileBusiness))
                                            : (isIOS(device) ? NearpeerMobileiOS : customGraphic || NearpeerMobile)}
                                        alt='Nearpeer screenshots on Android and iOS'
                                    />
                                </div>
                            <div className={Classes.homeActions}>
                                {!window.cordova && (
                                    <div className={Classes.getApps}>
                                        <div className={`${Classes.actionText} ${useWhiteLanding ? Classes.csun : ''}`}>Get the Nearpeer app{isParentView && ' (will look like the student version, that\'s OK)'}:</div>
                                        <div
                                            className={`${Classes.getAppsButtons} ${useWhiteLanding ? Classes.csun : ''} ${
                                                !isUnknown(device) && Classes.onPhone
                                            }`}
                                        >
                                            {(
                                                (isTeamsApp || isCompany) ? <a
                                                    onClick={() => analyticsTemplates.buttons('signup', slug ? `signup ${slug}: get teams ios app` : 'signup: get teams ios app')}
                                                    className={Classes.getAppsButton}
                                                    href='https://itunes.apple.com/us/app/nearpeer-for-teams/id6445984458'
                                                    rel='noreferrer'
                                                    target={'_blank'}
                                                    data-focus-outline='radius:5,padding:2'
                                                >
                                                    <img src={AppStore} alt='AppStore' />
                                                </a> : <a
                                                    onClick={() => analyticsTemplates.buttons('signup', slug ? `signup ${slug}: get ios app` : 'signup: get ios app')}
                                                    className={Classes.getAppsButton}
                                                    href='https://itunes.apple.com/us/app/nearpeer-for-college-students/id1300243604'
                                                    rel='noreferrer'
                                                    target={'_blank'}
                                                    data-focus-outline='radius:5,padding:2'
                                                >
                                                    <img src={AppStore} alt='AppStore' />
                                                </a>
                                            )}
                                            {(
                                                (isTeamsApp || isCompany) ? <a
                                                    onClick={() => analyticsTemplates.buttons('signup', slug ? `signup ${slug}: get android app` : 'signup: get android app')}
                                                    className={Classes.getAppsButton}
                                                    href='https://play.google.com/store/apps/details?id=net.nearpeer.app_business'
                                                    rel='noreferrer'
                                                    target={'_blank'}
                                                    data-focus-outline='radius:5,padding:2'
                                                >
                                                    <img src={PlayStore} alt='PlayStore' />
                                                </a> : <a
                                                    onClick={() => analyticsTemplates.buttons('signup', slug ? `signup ${slug}: get android app` : 'signup: get android app')}
                                                    className={Classes.getAppsButton}
                                                    href='https://play.google.com/store/apps/details?id=net.nearpeer.app'
                                                    rel='noreferrer'
                                                    target={'_blank'}
                                                    data-focus-outline='radius:5,padding:2'
                                                >
                                                    <img src={PlayStore} alt='PlayStore' />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className={Classes.signupContainer}>
                                    <StyledForm
                                        noValidate
                                        onSubmit={(evt) => {
                                            evt.preventDefault();
                                            submitOrError();
                                        }}
                                    >
                                        {secondaryText && (
                                            <SecondaryText className={`${Classes.actionText } ${useWhiteLanding ? Classes.csun : ''} `}>
                                                {secondaryText}
                                            </SecondaryText>
                                        )}
                                        <br />
                                        <Controller
                                            name='email'
                                            control={control}
                                            render={() => (
                                                <LoginTextField
                                                    id='email-input'
                                                    value={email}
                                                    type='email'
                                                    label='Email'
                                                    onBlur={showError('email')}
                                                    onFocus={showError('email', false)}
                                                    onChange={onEmailChange}
                                                    style={{ marginBottom: 10 }}
                                                    error={error('email')}
                                                    hasLightBackground={useWhiteLanding}
                                                    aria-describedby={error('email') ? getDescribedById('email') : null}
                                                    inputColor={useWhiteLanding && '#333333'}
                                                    highlightColor={useWhiteLanding && '#333333'}
                                                    errorColor={useWhiteLanding && 'rgb(255, 51, 51)'}
                                                />
                                            )}
                                        />

                                        {email.includes('@umsystem') && (
                                            <SchoolTip>
                                                Use a valid @student.umkc.edu or personal email on file.
                                                Using @umsystem.edu address is not permitted.
                                            </SchoolTip>
                                        )}
                                        {email.includes('@txstate') && (
                                            <SchoolTip>
                                                Use a valid personal email on file with TXST. Using
                                                @txstate.edu address is not permitted by TXST.
                                            </SchoolTip>
                                        )}
                                        {email.includes('@fresnostate') && (
                                            <SchoolTip>
                                                Use a valid @mail.fresnostate.edu email. Using
                                                @fresnostate.edu is not permitted.
                                            </SchoolTip>
                                        )}
                                        {emailErrorShow && customError && (
                                            <SchoolTip>
                                                {customError}
                                            </SchoolTip>
                                        )}
                                        {showLoyolaHintText && (
                                            <SchoolTip>
                                                Students should sign up with a @my.loyno.edu email
                                            </SchoolTip>
                                        )}

                                        {showSchoolList && <br />}
                                        {showSchoolList && (
                                            <Controller
                                                name='schoolId'
                                                control={control}
                                                render={() => (
                                                    <Autocomplete
                                                        options={filteredSchools}
                                                        inputValue={schoolSearchText}
                                                        onInputChange={onSchoolSearchTextChange}
                                                        value={filteredSchools.find((school) => school.id === schoolId) || null}
                                                        onChange={onSchoolChange}
                                                        getOptionLabel={({ name }) => name}
                                                        filterOptions={internals.filterOptions}
                                                        renderInput={(args) => (
                                                            <LoginTextField
                                                                {...args}
                                                                onBlur={showError('schoolId')}
                                                                onFocus={showError('schoolId', false)}
                                                                error={error('schoolId')}
                                                                helperText={error('schoolId')}
                                                                hasLightBackground={useWhiteLanding}
                                                                InputProps={{
                                                                    ...args.InputProps,
                                                                    endAdornment: null
                                                                }}
                                                                label={`${isTeamsApp ? 'Team' : 'College'} - Type to view list`}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            />
                                        )}
                                        {showSchoolList && <br />}

                                        <StyledButton
                                            type='submit'
                                            color='primary'
                                            disabled={isForbiddenEmail}
                                            $backgroundColor={useWhiteLanding && '#000000'}
                                            $textColor={useWhiteLanding && '#ffffff'}
                                            data-focus-outline='radius:40,padding:4'
                                        >
                                            Create Account
                                        </StyledButton>
                                        <StyledLink
                                            to='/'
                                            useWhiteLanding={useWhiteLanding}
                                            onClick={() => analyticsTemplates.buttons('login', 'signup: log in')}
                                        >
                                            Have an account? Log in
                                        </StyledLink>
                                        <StyledLink to='/login-help' useWhiteLanding={useWhiteLanding}>
                                            Trouble Logging In? Get Help
                                        </StyledLink>
                                    </StyledForm>
                                    <div className={`${Classes.actionSmallText } ${useWhiteLanding ? Classes.csun : ''} `}>{isParentView ?
                                        'Available for students, staff, and parents at participating colleges and universities' :
                                        (isCompany || isCommunity || isTeamsApp) ? '' : 'Available at participating colleges and universities'
                                    }</div>
                                </div>
                            </div>
                        </div>
                        {footer}
                    </div>

                    {isParentView && (
                        <ParentTertiaryText>
                            {tertiaryText}
                        </ParentTertiaryText>
                    )}

                    <Footer />
                    <Snackbar />
                </StyledPageBox>
            </ScrollContainerLayout>
        </Wrapper>
    );
};

Signup.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    platformName: PropTypes.string.isRequired,
    onInvalidSubmit: PropTypes.func.isRequired,
    schools: PropTypes.array.isRequired,
    signupDetails: PropTypes.shape({
        email: PropTypes.string,
        schoolId: PropTypes.number
    }),
    isCompany: PropTypes.bool,
    isCommunity: PropTypes.bool,
    showSchoolList: PropTypes.bool,
    text: PropTypes.string,
    secondaryText: PropTypes.string,
    tertiaryText: PropTypes.node,
    customGraphic: PropTypes.node,
    customError: PropTypes.node,
    useWhiteLanding: PropTypes.bool,
    useWideLogo: PropTypes.bool,
    useExtraWideLogo: PropTypes.bool,
    logoSidePadding: PropTypes.number,
    footer: PropTypes.any,
    logo: PropTypes.string,
    match: PropTypes.shape({
        params: PropTypes.shape({
            slug: PropTypes.string
        })
    }),
    isParentView: PropTypes.bool,
    push: PropTypes.func.isRequired,
    updateLoginContext: PropTypes.func.isRequired,
    updateSignupContext: PropTypes.func.isRequired,
    rememberedEmail: PropTypes.string
};

internals.noop = async () => undefined;

internals.nearpeerLogo = require('static/logos/nearpeer_logo.png');

internals.filterOptions = (options, { inputValue }) => (
    matchSorter(options, inputValue, { keys: ['name'] })
);

internals.getSchoolSearchText = ({ schools, schoolId }) => {

    if (!Array.isArray(schools)) {
        return '';
    }

    return (
        schools.find((school) => school.id === schoolId) || {}
    ).name || '';
};

internals.buildFormState = ({
    values,
    emailErrorShow,
    schoolIdErrorShow,
    schoolSearchText
}) => ({
    email: values.email || '',
    schoolId: values.schoolId || null,
    emailErrorShow,
    schoolIdErrorShow,
    schoolSearchText
});

internals.validateField = ({
    field,
    values,
    showSchoolList,
    schoolSearchText
}) => {

    switch (field) {
        case 'email':
            if (!values.email) {
                return 'Email is required';
            }

            if (!IsEmail(values.email)) {
                return 'Invalid email address';
            }

            break;

        case 'schoolId':
            if (showSchoolList && (!values.schoolId || !schoolSearchText)) {
                return `${process.env.APP_FLAVOUR === 'teams' ? 'Team' : 'College'} is required`;
            }

            break;
    }

    return null;
};

internals.SchoolTip = Styled(Typography)`
    color: orange;
    && {
        margin-top: ${({ theme }) => theme.spacing(2)};
        margin-bottom: 10px;
    }
`;

internals.StyledButton = Styled(Button)`
    border-radius: 40px;
    height: 3em;
    width: 100%;
    letter-spacing: 1px;

    &&& {
        background-color: ${(props) =>  props.backgroundColor || props.theme.palette.common.white};
        color: ${(props) =>  props.textColor || props.theme.palette.primary.main};
        margin-bottom: ${({ theme }) => theme.spacing(1)};
        font-family: ${Fonts.headerFont};
        font-weight: bold;
        text-transform: none;
        font-size: 1em;
        line-height: 3em;
    }
`;

internals.StyledLink = function Link(props) {

    const {
        to,
        useWhiteLanding,
        children,
        onClick
    } = props;

    return (
        <div className={Classes.linkHolder}>
            <NavLink
                className={Classes.link}
                style={{
                    textDecoration: 'none',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    padding: 0,
                    borderRadius: 0,
                    borderBottomColor: `${useWhiteLanding ? '#000000' : ''}`,
                    color: `${useWhiteLanding ? '#333333' : ''}`
                }}
                to={to}
                onClick={onClick}
                data-focus-outline='radius:4,padding:5'
            >
                {children}
            </NavLink>
        </div>
    );
};

internals.StyledLink.propTypes = {
    to: PropTypes.string,
    useWhiteLanding: PropTypes.bool,
    children: PropTypes.string,
    onClick: PropTypes.func
};

internals.Wrapper = Styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-flow: column nowrap;
    overflow: hidden;
`;

internals.StyledPageBox = Styled(Box)`
    background-color: ${({ $useWhiteLanding, theme }) => $useWhiteLanding ? '#ffffff' : theme.palette.primary.main};
`;

internals.WideLogoContainer = Styled.div`
    padding-left: ${({ $logoSidePadding }) => $logoSidePadding || 0}px;
    padding-right: ${({ $logoSidePadding }) => $logoSidePadding || 0}px;
`;

internals.StyledForm = Styled.form`
    && {
        margin: 0;
    }
`;

internals.SecondaryText = Styled.div`
    padding-bottom: 0;
    text-align: center;
`;

internals.ParentTertiaryText = Styled.div`
    color: white;
`;
